import type { ShadcnOpenApiSpec, ShadcnPageConfig } from '../types';

export interface EntityFieldSuggestion {
  name: string;
  sourceType: string;
  inferredType: 'text' | 'email' | 'number' | 'select';
  locked: boolean;
  enumValues?: string[];
}

export interface DiscoveredEntity {
  name: string;
  slug: string;
  endpoint: string;
  fields: EntityFieldSuggestion[];
}

export interface BuilderPageConfig extends ShadcnPageConfig {
  id: string;
  entity?: string;
  endpoint?: string;
}

interface SchemaObject {
  type?: string;
  format?: string;
  enum?: unknown[];
  properties?: Record<string, SchemaObject>;
}

interface GetPathItem {
  get?: {
    responses?: {
      '200'?: {
        content?: {
          'application/json'?: {
            schema?: {
              $ref?: string;
              items?: {
                $ref?: string;
              };
            };
          };
        };
      };
    };
  };
}

function normalizeRef(ref: string): string {
  return ref.split('/').pop() ?? ref;
}

function toSlug(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .toLowerCase();
}

function inferFieldSuggestion(name: string, schema: SchemaObject): EntityFieldSuggestion {
  const enumValues = Array.isArray(schema.enum) ? schema.enum.map(v => String(v)) : undefined;
  if (enumValues?.length) {
    return {
      name,
      sourceType: schema.type ?? 'string',
      inferredType: 'select',
      locked: true,
      enumValues,
    };
  }

  if (schema.format === 'email' || /email/i.test(name)) {
    return {
      name,
      sourceType: schema.type ?? 'string',
      inferredType: 'email',
      locked: true,
    };
  }

  if (schema.type === 'integer' || schema.type === 'number') {
    return {
      name,
      sourceType: schema.type,
      inferredType: 'number',
      locked: false,
    };
  }

  return {
    name,
    sourceType: schema.type ?? 'string',
    inferredType: 'text',
    locked: false,
  };
}

function findEndpoint(paths: Record<string, GetPathItem>, schemaName: string, slug: string): string {
  const candidates = Object.entries(paths);
  const exact = candidates.find(([path]) => path.includes(`/${slug}`) && /\{.+\}/.test(path) === false);
  if (exact) return exact[0];

  const bySchema = candidates.find(([, pathItem]) => {
    const getRef = pathItem.get?.responses?.['200']?.content?.['application/json']?.schema?.items?.$ref
      ?? pathItem.get?.responses?.['200']?.content?.['application/json']?.schema?.$ref;
    return typeof getRef === 'string' && normalizeRef(getRef) === schemaName;
  });

  return bySchema?.[0] ?? `/${slug}`;
}

export function discoverEntities(spec: ShadcnOpenApiSpec): DiscoveredEntity[] {
  const components = spec.components?.schemas;
  const paths = spec.paths ?? {};
  if (!components) return [];

  return Object.entries(components)
    .filter(([, schema]) => schema?.type === 'object' && schema.properties)
    .map(([name, schema]) => {
      const slug = toSlug(name);
      const endpoint = findEndpoint(paths, name, slug);
      const fields = Object.entries(schema.properties ?? {}).map(([fieldName, fieldSchema]) =>
        inferFieldSuggestion(fieldName, fieldSchema),
      );

      return {
        name,
        slug,
        endpoint,
        fields,
      };
    });
}

export function scaffoldCrudListPage(entity: DiscoveredEntity): BuilderPageConfig {
  return {
    id: `${entity.slug}-crud-list`,
    type: 'crud-list',
    title: `${entity.name} List`,
    path: `/${entity.slug}`,
    entity: entity.name,
    endpoint: entity.endpoint,
    table: {
      title: `${entity.name} Table`,
      columns: entity.fields.slice(0, 6).map(field => ({
        key: field.name,
        header: field.name,
      })),
      data: [],
      searchable: true,
    },
  };
}

export async function validatePagesAgainstApi(
  apiBaseUrl: string,
  pages: BuilderPageConfig[],
): Promise<Record<string, boolean>> {
  const entries = await Promise.all(
    pages.map(async page => {
      if (!page.endpoint) return [page.id, false] as const;
      try {
        const endpointUrl = new URL(page.endpoint, apiBaseUrl).toString();
        const response = await fetch(endpointUrl, { method: 'GET' });
        return [page.id, response.ok] as const;
      } catch {
        return [page.id, false] as const;
      }
    }),
  );

  return Object.fromEntries(entries);
}
